import { useState, useEffect, useRef } from 'react';
import { ProgressSpinner } from 'primereact/progressspinner';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Avatar } from 'primereact/avatar';
import { Chip } from 'primereact/chip';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useAxios from '../../hooks/useAxios';

const Staff = () => {
  const { user } = useAuth();
  const { fetchData: fetchStaffData, response: staffResponse, error: staffError } = useAxios();
  const { fetchData: deleteStaffData, response: deleteResponse, error: deleteError } = useAxios();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [filterDepartment, setFilterDepartment] = useState(null);
  const navigate = useNavigate();
  const toast = useRef(null);

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    setLoading(true);
    await fetchStaffData({ method: 'get', url: '/api/staff' });
  };

  // Handle staff response
  useEffect(() => {
    if (staffResponse) {
      const sorted = (staffResponse.data || []).sort((a, b) => {
        const lastA = (a.lastname || '').toLowerCase();
        const lastB = (b.lastname || '').toLowerCase();
        return lastA.localeCompare(lastB);
      });
      setEmployees(sorted);
      setLoading(false);
    }
  }, [staffResponse]);

  // Handle staff error
  useEffect(() => {
    if (staffError) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load staff' });
      setLoading(false);
    }
  }, [staffError]);

  const filteredEmployees = employees.filter(emp => {
    const q = search.trim().toLowerCase();

    if (!q) {
      const matchesDepartment = !filterDepartment || emp.department === filterDepartment;
      return matchesDepartment;
    }

    // Create full name string for mixed field search
    const fullName = `${emp.lastname || ''} ${emp.middlename || ''} ${emp.firstname || ''}`.toLowerCase();

    // Check if search query matches full name or any individual field
    const matchesSearch = fullName.includes(q) || [
      emp.email,
      emp.mobile,
      emp.department,
      emp.division,
      emp.position,
      emp.vpn,
    ].filter(Boolean).some(value => String(value).toLowerCase().includes(q));

    const matchesDepartment = !filterDepartment || emp.department === filterDepartment;

    return matchesSearch && matchesDepartment;
  });

  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];
  departments.sort();

  const handleDelete = (emp) => {
    confirmDialog({
      message: `Are you sure you want to delete ${emp.firstname} ${emp.lastname}?`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        await deleteStaffData({ method: 'delete', url: `/api/staff/${emp._id}` });
      }
    });
  };

  // Handle delete response
  useEffect(() => {
    if (deleteResponse) {
      toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Employee deleted successfully' });
      loadStaff();
    }
  }, [deleteResponse]);

  // Handle delete error
  useEffect(() => {
    if (deleteError) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete employee' });
    }
  }, [deleteError]);

  const handleEdit = (emp) => {
    navigate(`/staff/edit/${emp._id}`);
  };

  const handleViewDetails = (emp) => {
    setSelectedEmployee(emp);
    setShowDetailDialog(true);
  };

  const renderEmployeeCard = (emp) => (
    <div key={emp._id} className="col-12 sm:col-6 md:col-4 lg:col-3">
      <Card
        className="h-full cursor-pointer hover:shadow-4 transition-shadow transition-duration-200"
        onClick={() => handleViewDetails(emp)}
      >
        <div className="flex flex-column align-items-center text-center gap-3">
          <Avatar
            image={emp.image || null}
            label={!emp.image ? getInitials(emp.lastname, emp.firstname) : null}
            size="xlarge"
            shape="circle"
            style={!emp.image ? { backgroundColor: 'var(--primary-color)', color: '#fff' } : {}}
          />
          <div>
            <div className="font-bold text-lg text-900">{emp.lastname} {emp.firstname}</div>
            <div className="text-600 text-sm mt-1">{emp.position || '—'}</div>
            {emp.department && <Tag value={emp.department} className="mt-2" />}
          </div>
          {emp.mobile && (
            <div className="text-600 text-sm">
              <i className="pi pi-phone mr-1" />{emp.mobile}
            </div>
          )}
          <div className="flex gap-2 mt-1">
            <Button
              icon="pi pi-pencil"
              rounded text severity="info" size="small"
              onClick={(e) => { e.stopPropagation(); handleEdit(emp); }}
              tooltip="Edit" tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-trash"
              rounded text severity="danger" size="small"
              onClick={(e) => { e.stopPropagation(); handleDelete(emp); }}
              tooltip="Delete" tooltipOptions={{ position: 'bottom' }}
            />
          </div>
        </div>
      </Card>
    </div>
  );

  const getInitials = (firstname, lastname) => {
    return `${firstname?.charAt(0) || ''}${lastname?.charAt(0) || ''}`.toUpperCase();
  };

  const renderDetailDialog = () => {
    if (!selectedEmployee) return null;
    const fields = [
      { icon: 'pi-envelope', label: 'Email', value: selectedEmployee.email },
      { icon: 'pi-phone', label: 'Mobile', value: selectedEmployee.mobile },
      { icon: 'pi-building', label: 'Department', value: selectedEmployee.department },
      { icon: 'pi-sitemap', label: 'Division', value: selectedEmployee.division },
      { icon: 'pi-id-card', label: 'Position', value: selectedEmployee.position },
      { icon: 'pi-shield', label: 'VPN', value: selectedEmployee.vpn },
      { icon: 'pi-user', label: 'Gender', value: selectedEmployee.sex === 'М' ? 'Male' : selectedEmployee.sex === 'Ж' ? 'Female' : selectedEmployee.sex },
    ];
    return (
      <Dialog
        header={
          <div>
            <div className="font-bold text-xl">{selectedEmployee.firstname} {selectedEmployee.middlename === 'Null' ? selectedEmployee.middlename : ''}{selectedEmployee.lastname}</div>
            <div className="text-600">{selectedEmployee.position}</div>
          </div>
        }
        visible={showDetailDialog}
        style={{ width: '600px' }}
        breakpoints={{ '640px': '95vw' }}
        onHide={() => setShowDetailDialog(false)}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button label="Edit" icon="pi pi-pencil" onClick={() => { setShowDetailDialog(false); handleEdit(selectedEmployee); }} />
            <Button label="Delete" icon="pi pi-trash" severity="danger" outlined onClick={() => { setShowDetailDialog(false); handleDelete(selectedEmployee); }} />
          </div>
        }
      >
        <div className="flex flex-column align-items-center mb-4">
          {selectedEmployee.image ? (
            <img
              src={selectedEmployee.image}
              alt={`${selectedEmployee.firstname} ${selectedEmployee.lastname}`}
              style={{ width: '200px', height: '200px', objectFit: 'cover', borderRadius: '50%' }}
            />
          ) : (
            <Avatar
              label={getInitials(selectedEmployee.firstname, selectedEmployee.lastname)}
              size="xlarge" shape="circle"
              style={{ backgroundColor: 'var(--primary-color)', color: '#fff', fontSize: '3rem' }}
            />
          )}
        </div>
        <div className="grid">
          {fields.filter(f => f.value).map(f => (
            <div key={f.label} className="col-6 mb-3">
              <div className="text-500 text-sm mb-1"><i className={`pi ${f.icon} mr-1`} />{f.label}</div>
              <div className="font-medium text-900">{f.value}</div>
            </div>
          ))}
        </div>
      </Dialog>
    );
  };

  return (
    <div className="surface-ground min-h-screen p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      {/* Header */}
      <div className="flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold m-0 text-900">
            <i className="pi pi-users mr-2 text-primary" />Team Directory
          </h1>
          <div className="flex gap-2 mt-2">
            <Chip label={`${filteredEmployees.length} Members`} icon="pi pi-users" />
            <Chip label={`${departments.length} Departments`} icon="pi pi-building" />
          </div>
        </div>
        <Button label="Add Member" icon="pi pi-plus" onClick={() => navigate('/staff/new')} />
      </div>

      {/* Toolbar */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <span className="p-input-icon-left flex-1" style={{ minWidth: '240px' }}>
          <InputText
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, department..."
            className="w-full"
          />
        </span>
        <Dropdown
          value={filterDepartment}
          options={departments.map(d => ({ label: d, value: d }))}
          onChange={(e) => setFilterDepartment(e.value)}
          placeholder="All Departments"
          showClear
          style={{ minWidth: '200px' }}
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <ProgressSpinner />
        </div>
      )}

      {/* Empty */}
      {!loading && filteredEmployees.length === 0 && (
        <div className="flex flex-column align-items-center justify-content-center py-8 text-center">
          <i className="pi pi-users text-6xl text-300 mb-4" />
          <h3 className="text-xl text-600 m-0 mb-2">No team members found</h3>
          <p className="text-500 mb-4">{search ? 'Try adjusting your search or filters' : 'Start building your team'}</p>
          {!search && <Button label="Add First Member" icon="pi pi-plus" onClick={() => navigate('/staff/new')} />}
        </div>
      )}

      {/* Grid */}
      {!loading && filteredEmployees.length > 0 && (
        <div className="grid">
          {filteredEmployees.map(emp => renderEmployeeCard(emp))}
        </div>
      )}

      {renderDetailDialog()}
    </div>
  );
};

export default Staff;
